import {Gallery} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/gallery.types';
import {ProductImageTypes} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_images.types';
import {PriceAndBrandSection_BrandLogoSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {
  useIsInitialLocation,
  usePathname,
  useRouteEntities,
  useSearch,
} from '@aboutyou/router';
import {startTransition, useEffect, useRef} from 'react';
import {isProductTileEntities} from '../../../features/ProductTile/isProductTileEntities';
import type {ProductTileEntities} from '../../../features/ProductTile/types';

export const useProductTileEntities = () => {
  const entities =
    useRouteEntities<ProductTileEntities>() as ProductTileEntities;

  const {
    primaryBustImageGallery,
    primaryModelImageGallery,
    preferredProductImageType,
    gallery,
    gridColumns,
  } = entities;

  const imageGallery =
    preferredProductImageType === ProductImageTypes.MODEL
      ? primaryModelImageGallery
      : primaryBustImageGallery;

  const entitiesGallery: Gallery = gallery?.elements?.length
    ? gallery
    : {
        elements: (imageGallery || []).map(image => ({
          type: {
            $case: 'image',
            image: {
              image,
            },
          },
        })),
        initialElementIndex: 0,
      };

  const productTileEntitiesExist = isProductTileEntities(entities);
  const isInitialLocation = useIsInitialLocation();
  const pathname = usePathname();
  const search = useSearch();
  const locationKey = pathname + search;
  const previousLocationKey = useRef<null | string>(locationKey);
  const animatePageElements = !isInitialLocation && productTileEntitiesExist;
  const loadingStateDismissed = useRef(false);

  const [showFromProductTileEntities, setShowFromProductTileEntities] =
    useHistoryPageState(
      productTileEntitiesExist &&
        !isInitialLocation &&
        previousLocationKey.current !== locationKey,
      {
        uniqueKey: 'showFromProductTileEntities',
      },
    );

  useEffect(() => {
    startTransition(() => {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setShowFromProductTileEntities(false);
      loadingStateDismissed.current = true;
    });
  }, [setShowFromProductTileEntities]);

  return {
    showFromProductTileEntities:
      showFromProductTileEntities && !loadingStateDismissed.current,
    animatePageElements,
    entitiesGallery,
    productImage:
      gallery?.elements[0]?.type?.$case === 'image' &&
      gallery?.elements[0]?.type.image.image,
    entitiesPrice: entities.price,
    entitiesBrandLogo: {
      brand: entities.brand,
    } as PriceAndBrandSection_BrandLogoSection,
    entitiesProduct: entities.product,
    entitiesGridColumns: gridColumns,
  };
};
